import { useEffect, useState } from "react";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";
import {
  Calendar,
  FileText,
  Award,
  Save,
  X,
  Check,
  AlertCircle,
  GraduationCap,
  Globe,
} from "lucide-react";
import { toast } from "react-toastify";
import TopBarComponent from "../TopBarComponent";
import SidebarComponent from "../SidbarComponent";
import { FooterComponents } from "../FooterComponent";
import { countries } from "../data/countries";

export default function ScholarshipForm() {
  const [academicYears, setAcademicYears] = useState([]);



  // Fetch academic years from backend
  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `hec/academic/getAcademicYears`
      );
      // Assuming the response data is an array of academic year objects
      return response.data;
    } catch (error) {
      console.error("Failed to fetch academic years:", error);
      return [];
    }
  };

  // Example usage with useEffect:
  useEffect(() => {
    fetchAcademicYears().then(setAcademicYears);
  }, []);

  // Sort academic years in descending order (most recent first)
  const sortedAcademicYears = academicYears.sort(
    (a, b) => parseInt(b.academicYearCode) - parseInt(a.academicYearCode)
  );

  const [formData, setFormData] = useState({
    academicYearId: "",
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    scholarshipType: "",
    countryName: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle scholarship type change
    if (name === "scholarshipType") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        // Automatically set country to Rwanda for Government scholarships
        countryName: value === "GOVERNMENT" ? "Rwanda" : prev.countryName,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const validateForm = () => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!formData.academicYearId) {
      newErrors.academicYearId = "Academic year is required";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Scholarship name is required";
    } else if (formData.name.length < 5) {
      newErrors.name = "Scholarship name must be at least 5 characters";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.length < 20) {
      newErrors.description = "Description must be at least 20 characters";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    } else if (new Date(formData.startDate) < today) {
      newErrors.startDate = "Start date cannot be in the past";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    } else if (new Date(formData.endDate) < today) {
      newErrors.endDate = "End date cannot be in the past";
    } else if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.endDate) <= new Date(formData.startDate)
    ) {
      newErrors.endDate = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Send formData to backend using axios
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}hec/abroad/create_abroad_scholarship?academicYear=${formData.academicYearId}`,
        {
          name: formData.name,
          description: formData.description,
          startDate: formData.startDate,
          scholarshipType: formData.scholarshipType,
          endDate: formData.endDate,
          countryName: formData.countryName,
        },
        { headers: getAuthHeaders() }
      );

      setSubmitSuccess(true);

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          academicYearId: "",
          name: "",
          description: "",
          startDate: "",
          endDate: "",
          requirementInput: "",
          scholarshipType: "",
        });
        setSubmitSuccess(false);
      }, 3000);
      // A toast notification here using a library like react-toastify
      toast.success("Scholarship published successfully", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (error) {
      setErrors({ submit: "Failed to submit. Please try again." });
      toast.error("Failed to create scholarship. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      academicYearId: "",
      name: "",
      description: "",
      startDate: "",
      endDate: "",
      countryName: "",
    });
    setErrors({});
    setSubmitSuccess(false);
  };

  const getSelectedAcademicYearName = () => {
    if (!formData.academicYearId) return "";
    const selectedYear = academicYears.find(
      (year) => year.id === formData.academicYearId
    );
    return selectedYear ? selectedYear.academicYearName : "";
  };

  return (
    <div className="bg-gray-50">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700"
        aria-label="Sidebar"
      >
        <SidebarComponent />
      </aside>
      <div className="pt-6 sm:ml-64">
        <div className="min-h-screen p-6">
          <div className="max-w-4xl mx-auto">
            {/* Main Content: Centered Form */}
            <div className="flex justify-center">
              {/* Form Section */}
              <div className="w-full max-w-3xl">
                {/* Success Message */}
                <div className="text-center mt-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-cyan-800 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h1 className="text-xl font-bold  mb-2">
                    Create Scholarship Opportunity
                  </h1>
                </div>
                {/* Form */}
                <div className="bg-white rounded-xl shadow-lg border border-cyan-200 overflow-hidden">
                  <div className="p-8">
                    {/* Academic Year Dropdown */}
                    <div className="flex flex-row gap-6 mb-6">
                      {/* Academic Year Dropdown */}
                      <div className="flex-1">
                        <label
                          htmlFor="academicYearId"
                          className="block text-sm font-medium text-cyan-700 mb-2"
                        >
                          <div className="flex items-center space-x-2">
                            <GraduationCap className="w-4 h-4 text-cyan-400" />
                            <span>Academic Year</span>
                            <span className="text-red-500">*</span>
                          </div>
                        </label>
                        <select
                          id="academicYearId"
                          name="academicYearId"
                          value={formData.academicYearId}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 ${
                            errors.academicYearId
                              ? "border-red-300 bg-red-50"
                              : "border-cyan-300"
                          }`}
                        >
                          <option value="">Select an academic year...</option>
                          {sortedAcademicYears.map((year) => (
                            <option key={year.id} value={year.id}>
                              {year.academicYearName}
                            </option>
                          ))}
                        </select>
                        {errors.academicYearId && (
                          <div className="flex items-center space-x-1 mt-2 text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">
                              {errors.academicYearId}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Scholarship Name */}
                      <div className="flex-1">
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-cyan-700 mb-2"
                        >
                          <div className="flex items-center space-x-2">
                            <Award className="w-4 h-4 text-cyan-400" />
                            <span>Scholarship Name</span>
                            <span className="text-red-500">*</span>
                          </div>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 ${
                            errors.name
                              ? "border-red-300 bg-red-50"
                              : "border-cyan-300"
                          }`}
                          placeholder="Enter scholarship name..."
                        />
                        {errors.name && (
                          <div className="flex items-center space-x-1 mt-2 text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">{errors.name}</span>
                          </div>
                        )}
                        </div>
                      </div>

                        {/*add scholarship type */}
                        <div className="flex-1 mb-3">
                          <label
                            htmlFor="scholarshipType"
                            className="block text-sm font-medium text-cyan-700 mb-2"
                          >
                            <div className="flex items-center space-x-2">
                              <Award className="w-4 h-4 text-cyan-400" />
                              <span>Scholarship Type</span>
                              <span className="text-red-500">*</span>
                            </div>
                          </label>
                          <select
                            id="scholarshipType"
                            name="scholarshipType"
                            value={formData.scholarshipType || ""}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 border-cyan-300"
                          >
                            <option value="">Select type...</option>
                            <option value="GOVERNMENT">GOVERNMENT</option>
                            <option value="COOPERATION">COOPERATION</option>
                          </select>
                        </div>
                        
                        {/* Country Selection */}
                        <div className="flex-1 mb-3">
                          <label
                            htmlFor="countryName"
                            className="block text-sm font-medium text-cyan-700 mb-2"
                          >
                            <div className="flex items-center space-x-2">
                              <Globe className="w-4 h-4 text-cyan-400" />
                              <span>Country</span>
                              <span className="text-red-500">*</span>
                            </div>
                          </label>
                          <select
                            id="countryName"
                            name="countryName"
                            value={formData.countryName || ""}
                            onChange={handleInputChange}
                            disabled={formData.scholarshipType === "GOVERNMENT"}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 ${
                              formData.scholarshipType === "GOVERNMENT" 
                                ? "bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300"
                                : errors.countryName
                                ? "border-red-300 bg-red-50"
                                : "border-cyan-300"
                            }`}
                          >
                            <option value="">Select a country...</option>
                            {countries.map((country) => (
                              <option key={country.code} value={country.name}>
                                {country.name}
                              </option>
                            ))}
                          </select>
                          {errors.countryName && (
                            <div className="flex items-center space-x-1 mt-2 text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              <span className="text-sm">{errors.countryName}</span>
                            </div>
                          )}
                        </div>

                        {/* Description */}
                    <div className="mb-3">
                      <label
                        htmlFor="description" dd
                        className="block text-sm font-medium text-cyan-700 mb-2"
                      >
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-cyan-400" />
                          <span>Description</span>
                          <span className="text-red-500">*</span>
                        </div>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={5}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 resize-none ${
                          errors.description
                            ? "border-red-300 bg-red-50"
                            : "border-cyan-300"
                        }`}
                        placeholder="Enter scholarship description..."
                      />
                      <div className="flex justify-between items-center mt-2">
                        {errors.description ? (
                          <div className="flex items-center space-x-1 text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">
                              {errors.description}
                            </span>
                          </div>
                        ) : (
                          <div></div>
                        )}
                        <span className="text-xs text-cyan-500">
                          {formData.description.length} characters
                        </span>
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-3">
                      {/* Start Date */}
                      <div>
                        <label
                          htmlFor="startDate"
                          className="block text-sm font-medium text-cyan-700 mb-2"
                        >
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-cyan-400" />
                            <span>Start Date</span>
                            <span className="text-red-500">*</span>
                          </div>
                        </label>
                        <input
                          type="date"
                          id="startDate"
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleInputChange}
                          min={getTodayDate()}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 ${
                            errors.startDate
                              ? "border-red-300 bg-red-50"
                              : "border-cyan-300"
                          }`}
                        />
                        {errors.startDate && (
                          <div className="flex items-center space-x-1 mt-2 text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">{errors.startDate}</span>
                          </div>
                        )}
                      </div>

                      {/* End Date */}
                      <div>
                        <label
                          htmlFor="endDate"
                          className="block text-sm font-medium text-cyan-700 mb-2"
                        >
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-cyan-400" />
                            <span>End Date</span>
                            <span className="text-red-500">*</span>
                          </div>
                        </label>
                        <input
                          type="date"
                          id="endDate"
                          name="endDate"
                          value={formData.endDate}
                          onChange={handleInputChange}
                          min={formData.startDate || getTodayDate()}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 ${
                            errors.endDate
                              ? "border-red-300 bg-red-50"
                              : "border-cyan-300"
                          }`}
                        />
                        {errors.endDate && (
                          <div className="flex items-center space-x-1 mt-2 text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">{errors.endDate}</span>
                          </div>
                        )}
                      </div>
                    </div>
                   

                    {/* Duration Display */}
                    {formData.startDate &&
                      formData.endDate &&
                      !errors.endDate && (
                        <div className="mb-6 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                          <div className="flex items-center space-x-2 text-cyan-800">
                            <Calendar className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              Duration:{" "}
                              {Math.ceil(
                                (new Date(formData.endDate) -
                                  new Date(formData.startDate)) /
                                  (1000 * 60 * 60 * 24)
                              )}{" "}
                              days
                            </span>
                          </div>
                        </div>
                      )}
                  </div>

                  {/* Form Actions */}
                  <div className="bg-cyan-50 px-8 py-6 border-t border-cyan-200">
                    <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                      <button
                        type="button"
                        onClick={handleReset}
                        className="flex items-center justify-center px-6 py-3 border border-cyan-300 rounded-lg text-cyan-700 hover:bg-cyan-100 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all duration-200"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reset
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-cyan-800 to-cyan-600 text-white rounded-lg hover:from-cyan-900 hover:to-cyan-700 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Creating...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Create Scholarship
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <FooterComponents />
        </div>
      </div>
    </div>
  );
}
